"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Search,
  Calendar,
  User,
  Tag,
  Clock,
  ArrowRight,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { formatCalendarDate, getCalendarYear } from "@/utils/date";

interface Blog {
  _id?: string;
  id?: string;
  title: string;
  content: string;
  description: string;
  status: "draft" | "published" | "scheduled";
  author: string;
  tags: string[];
  coverImageUrl?: string;
  coverImageAlt?: string;
  views: number;
  createdAt?: string;
  updatedAt?: string;
  publishDate?: string;
  slug?: string;
}

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  status: "draft" | "published" | "scheduled";
  publishDate: string;
  lastModified: string;
  tags: string[];
  featuredImage?: string;
  views: number;
  filename: string;
  readTime?: number;
  slug?: string;
}

const BlogHomepage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState<string>("");

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(6); // Show 6 posts per page
  
  const router = useRouter();

  // Convert Blog to BlogPost format
  const convertBlogToPost = (blog: Blog): BlogPost => {
    // Calculate read time based on content length
    const contentText = blog.content?.replace(/<[^>]*>/g, "") || "";
    const wordCount = contentText
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    const readTime = Math.max(1, Math.ceil(wordCount / 200));

    return {
      id: blog.id || blog._id?.toString() || "",
      title: blog.title || "Untitled Post",
      content: blog.content || "",
      excerpt: blog.description || "No description available.",
      author: blog.author || "Smile4Me Team",
      status: blog.status || "published",
      // Use publishDate if available, otherwise fall back to createdAt
      publishDate: blog.publishDate || 
        (blog.createdAt ? new Date(blog.createdAt).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]),
      lastModified: blog.updatedAt
        ? new Date(blog.updatedAt).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      tags: Array.isArray(blog.tags) ? blog.tags : [],
      featuredImage: blog.coverImageUrl || "",
      views: blog.views || 0,
      filename: blog.slug || blog.id || blog._id?.toString() || "",
      readTime,
      slug: blog.slug,
    };
  };

  // Fetch blog posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        setError("");

        const response = await fetch("/api/blogs");
        if (!response.ok) {
          throw new Error("Failed to fetch blogs");
        }

        const blogs: Blog[] = await response.json();

        // Filter for published blogs only
        const publishedBlogs = blogs.filter(
          (blog) => blog.status === "published"
        );

        // Convert to BlogPost format and sort by publish date (newest first)
        const fetchedPosts: BlogPost[] = publishedBlogs
          .map(convertBlogToPost)
          .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());

        setPosts(fetchedPosts);
        setFilteredPosts(fetchedPosts);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError("Failed to load blog posts. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Filter posts based on search and year
  useEffect(() => {
    let filtered = posts;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(searchLower) ||
          post.excerpt.toLowerCase().includes(searchLower) ||
          post.author.toLowerCase().includes(searchLower)
      );
    }

    if (selectedYear) {
      filtered = filtered.filter((post) => {
        const postYear = getCalendarYear(post.publishDate);
        return postYear === selectedYear;
      });
    }

    setFilteredPosts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [posts, searchTerm, selectedYear]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);

  // Get all unique years from posts
  const availableYears = Array.from(
    new Set(
      posts.map((post) => getCalendarYear(post.publishDate)).filter(Boolean)
    )
  ).sort((a, b) => parseInt(b) - parseInt(a)); // Sort newest first

  // const formatViews = (views: number) => {
  //   if (views >= 1000) {
  //     return `${(views / 1000).toFixed(1)}k`;
  //   }
  //   return views.toString();
  // };

  const handlePostClick = async (post: BlogPost) => {
    try {
      // Increment view count (optional - you can create this API endpoint if needed)
      if (post.id) {
        fetch(`/api/blogs/${post.id}/views`, {
          method: "POST",
        }).catch((error) => {
          console.error("Error incrementing views:", error);
        });
      }

      // Navigate to blog post (use slug if available, otherwise use id)
      const identifier = post.slug || post.filename || post.id;
      router.push(`/blog/${identifier}`);
    } catch (error) {
      console.error("Error handling post click:", error);
      // Still navigate even if view increment fails
      const identifier = post.slug || post.filename || post.id;
      router.push(`/blog/${identifier}`);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedYear("");
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of blog posts
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) pageNumbers.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 sm:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#AE7D2F] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Loading Blog Posts
            </h1>
            <p className="text-gray-600">
              Fetching the latest content for you...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 sm:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] text-white rounded-lg hover:from-[#9A6B26] hover:to-[#D4B84F] transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 pt-32 sm:pt-24">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                Smile4Me <span className="text-[#AE7D2F]">Blog</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mt-4">
                Your guide to optimal oral health and beautiful smiles. Expert
                dental advice, patient care tips, and the latest in modern
                dentistry from our experienced team.
              </p>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Search and Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search articles or authors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#AE7D2F] focus:border-[#AE7D2F] outline-none transition-colors"
                />
              </div>

              {/* Year Filter */}
              <div className="relative lg:w-64">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="appearance-none w-full bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-[#AE7D2F] focus:border-[#AE7D2F] outline-none cursor-pointer transition-colors"
                >
                  <option value="">All Years ({posts.length})</option>
                  {availableYears.map((year) => {
                    const count = posts.filter((post) => 
                      getCalendarYear(post.publishDate) === year
                    ).length;
                    return (
                      <option key={year} value={year}>
                        {year} ({count})
                      </option>
                    );
                  })}
                </select>
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Active Filters */}
            {(searchTerm || selectedYear) && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">
                  Active filters:
                </span>
                {searchTerm && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#AE7D2F]/10 text-[#AE7D2F] border border-[#AE7D2F]/20">
                    Search: &quot;{searchTerm}&quot;
                    <button
                      onClick={() => setSearchTerm("")}
                      className="ml-2 hover:bg-[#AE7D2F]/20 rounded-full w-4 h-4 flex items-center justify-center transition-colors"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedYear && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-200">
                    Year: {selectedYear}
                    <button
                      onClick={() => setSelectedYear("")}
                      className="ml-2 hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center transition-colors"
                      aria-label="Clear year filter"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Results Count and Pagination Info */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-gray-600">
              {filteredPosts.length === posts.length
                ? `Showing all ${posts.length} ${
                    posts.length === 1 ? "article" : "articles"
                  }`
                : `Found ${filteredPosts.length} of ${posts.length} ${
                    posts.length === 1 ? "article" : "articles"
                  }`}
            </p>
            {totalPages > 1 && (
              <p className="text-sm text-gray-500">
                Page {currentPage} of {totalPages} • Showing {startIndex + 1}-{Math.min(endIndex, filteredPosts.length)} of {filteredPosts.length}
              </p>
            )}
          </div>

          {/* Blog Posts List */}
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                {posts.length === 0 ? (
                  <Tag className="w-12 h-12 text-gray-400" />
                ) : (
                  <Search className="w-12 h-12 text-gray-400" />
                )}
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {posts.length === 0 ? "No blog posts yet" : "No articles found"}
              </h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {posts.length === 0
                  ? "Be the first to create a blog post! Start sharing your knowledge with the world."
                  : "Try adjusting your search terms or browse all articles."}
              </p>
              {posts.length === 0 ? (
                <Link
                  href="/admin/blog/new"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] text-white rounded-lg hover:from-[#9A6B26] hover:to-[#D4B84F] transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Create First Post
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              ) : (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  View All Articles
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-col space-y-5">
                {currentPosts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-white border border-gray-200/50 p-5 box-border transition-all duration-400 shadow-[0px_0px_20px_rgba(34,34,34,0.05)] hover:border-[#222] hover:shadow-[0px_0px_40px_rgba(34,34,34,0.2)] cursor-pointer group"
                    onClick={() => handlePostClick(post)}
                  >
                    <div className="flex flex-col lg:flex-row items-center">
                      {/* Featured Image */}
                      <div className="w-full h-[300px] lg:w-[300px] lg:h-[300px] bg-cover bg-center mr-0 lg:mr-10 flex-shrink-0 mb-6 lg:mb-0">
                        {post.featuredImage ? (
                          <Image
                            src={post.featuredImage}
                            alt={post.title}
                            width={300}
                            height={300}
                            className="w-full h-full object-cover"
                            sizes="(max-width: 1024px) 100vw, 300px"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#AE7D2F]/10 to-[#ECD05B]/20 flex items-center justify-center">
                            <Tag className="w-16 h-16 text-[#AE7D2F]/40" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        {/* Title */}
                        <h2 className="text-2xl font-semibold mb-4 group-hover:text-[#555555] transition-colors inline-block">
                          <Link
                            href={`/blog/${
                              post.slug || post.filename || post.id
                            }`}
                            className="hover:text-[#555555] transition-colors"
                          >
                            {post.title}
                            <div className="h-0.5 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] mt-2"></div>
                          </Link>
                        </h2>

                        {/* Meta Information */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4" />
                            <span>{formatCalendarDate(post.publishDate)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>{post.author}</span>
                          </div>
                          {post.readTime && (
                            <div className="flex items-center space-x-2">
                              <Clock className="w-4 h-4" />
                              <span>{post.readTime} min read</span>
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        <div className="text-gray-600 leading-relaxed mb-6 group-hover:text-[#d5ac26] transition-colors">
                          <p>{post.excerpt}</p>
                        </div>

                        {/* Read More Button */}
                        <div className="flex items-center space-x-2 text-[#d5ac26] border-0 p-0 text-xs uppercase font-bold bg-transparent hover:bg-transparent hover:text-[#d5ac26] transition-colors leading-4 group-hover:text-[#d5ac26]">
                          <span>Read More</span>
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center space-x-2 mt-12">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span className="hidden sm:block">Previous</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {getPageNumbers().map((pageNum, index) => (
                      <React.Fragment key={index}>
                        {pageNum === '...' ? (
                          <span className="px-3 py-2 text-gray-400">...</span>
                        ) : (
                          <button
                            onClick={() => handlePageChange(pageNum as number)}
                            className={`px-3 py-2 rounded-lg transition-colors ${
                              currentPage === pageNum
                                ? 'bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] text-white shadow-sm'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="hidden sm:block">Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default BlogHomepage;
